import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { ConfirmDialog, Textarea } from '@jumpstone/ui-react';
import type {
  LeaveActionFailure,
  LeaveActionResult,
} from '../../data/use_leave_requests';
import type { LeaveRequest } from '../../data/wagepro_store_leave';

/** Same soft cap on rejection notes as `wp.expenses.reject_reason_placeholder`. */
const MAX_REJECTION_REASON_LENGTH = 1000;

/**
 * Reject confirmation dialog. Unlike the expenses queue's reject dialog,
 * `payroll.rejectLeave.rejection_reason` is `z.string().optional()` — a
 * blank reason is a valid, permitted reject on the backend. The reason
 * field is therefore optional; the confirm button is enabled with an
 * empty reason. When the field IS non-empty we still enforce the same
 * length cap the backend implies.
 */
export function LeaveRejectDialog({
  request,
  onOpenChange,
  reject,
  onFailure,
}: {
  /** The request being rejected, or `null` when the dialog is closed. */
  request: LeaveRequest | null;
  onOpenChange: (open: boolean) => void;
  reject:
    | ((
        request_id: string,
        rejection_reason: string | undefined,
      ) => Promise<LeaveActionResult>)
    | undefined;
  /** `request_id` is passed explicitly — see the matching prop docblock on `LeaveApproveConfirmDialog`. */
  onFailure: (request_id: string, failure: LeaveActionFailure) => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [reason, set_reason] = useState('');
  const [is_submitting, set_is_submitting] = useState(false);

  const trimmed = reason.trim();

  const handle_confirm = (): void => {
    if (!request || !reject) return;
    const in_flight_id = request.id;
    set_is_submitting(true);
    void reject(in_flight_id, trimmed.length > 0 ? trimmed : undefined)
      .then((result) => {
        if (result.outcome === 'success') {
          set_reason('');
          onOpenChange(false);
        } else {
          // Close on failure so the row-level error is not obscured by the
          // modal backdrop — mirrors `LeaveApproveConfirmDialog`.
          set_reason('');
          onFailure(in_flight_id, result.failure);
        }
      })
      .finally(() => {
        set_is_submitting(false);
      });
  };

  return (
    <ConfirmDialog
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) {
          set_reason('');
        }
        onOpenChange(open);
      }}
      title={t('leave.reject_dialog_title')}
      description={t('leave.reject_dialog_description')}
      confirmLabel={t('common.reject')}
      cancelLabel={t('common.cancel')}
      variant="destructive"
      loading={is_submitting}
      onConfirm={handle_confirm}
    >
      <Textarea
        value={reason}
        onChange={(event) => {
          set_reason(event.target.value);
        }}
        placeholder={t('leave.reject_reason_placeholder')}
        aria-label={t('leave.reject_reason_label')}
        maxLength={MAX_REJECTION_REASON_LENGTH}
        disabled={is_submitting}
      />
      <p className="nv-help mt-1">{t('leave.reject_reason_optional_help')}</p>
    </ConfirmDialog>
  );
}
